import { getToken, onMessage } from 'firebase/messaging';
import { useEffect } from 'react';

import { messaging } from '@/firebase';
import { useRegisterPushMutation } from '@/shared/api/requests/push';
import { useProfileStore } from '@/shared/store/profileStore';
import { urlBase64ToUint8Array } from '@/shared/utils/urlBase64';

export const usePushNotifications = () => {
  const { isAuthenticated } = useProfileStore();
  const registerMutation = useRegisterPushMutation();

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!('Notification' in window)) return;

    let pushRegistration: ServiceWorkerRegistration | undefined;

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/firebase-messaging-sw.js', {
          scope: '/firebase-messaging/'
        })
        .catch(() => {});

      navigator.serviceWorker
        .register('/web-push-sw.js', { scope: '/web-push/' })
        .then((reg) => {
          pushRegistration = reg;
        })
        .catch(() => {});

      navigator.serviceWorker.register('/web-push-sw.js').catch(() => {});
    }

    const enable = async () => {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;
      try {
        const publicKey = import.meta.env.VITE_WEB_PUSH_PUBLIC_KEY as string;
        if (publicKey) {
          if (!pushRegistration) return;
          const subscription = await pushRegistration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey)
          });
          await registerMutation.mutateAsync({
            params: {
              token: JSON.stringify(subscription),
              platform: 'webpush'
            }
          });
        } else {
          const token = await getToken(messaging);
          if (token) {
            await registerMutation.mutateAsync({
              params: { token, platform: 'web' }
            });
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Push notification registration failed', err);
      }
    };

    enable();

    const unsub = onMessage(messaging, (payload) => {
      navigator.serviceWorker.getRegistration().then((reg) => {
        reg?.showNotification(payload.notification?.title ?? '', {
          body: payload.notification?.body,
          data: payload.data
        });
      });
    });

    return () => {
      unsub();
    };
  }, [isAuthenticated]);
};
